import { Badge, Button, Input, Label } from "@exhousing/design-system";
import { useState } from "react";
import { toast } from "sonner";

import { ConfirmDialog } from "@/components/ConfirmDialog";
import { Drawer } from "@/components/Drawer";
import { NotesTimeline } from "@/components/crm/NotesTimeline";
import {
  useConvertLead,
  useCrmLookups,
  useLead,
  useUpdateLead,
} from "@/hooks/api/useCrm";
import { useUsers } from "@/hooks/api/useUsers";
import type { components } from "@/types/api";

type Lead = components["schemas"]["LeadResponse"];

const SELECT =
  "h-9 w-full rounded-md border border-input bg-card px-2.5 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";

function LeadBody({ lead, onClose }: { lead: Lead; onClose: () => void }) {
  const update = useUpdateLead(lead.id);
  const convert = useConvertLead();
  const { data: stages } = useCrmLookups("lead_type");
  const { data: users } = useUsers();
  const [confirmConvert, setConfirmConvert] = useState(false);

  const initial = {
    first_name: lead.first_name ?? "",
    last_name: lead.last_name ?? "",
    email: lead.email ?? "",
    telephone: lead.telephone ?? "",
    lead_type_id: lead.lead_type_id ?? "",
    owner_user_id: lead.owner_user_id ?? "",
    object_city: lead.object_city ?? "",
    price_indication: lead.price_indication != null ? String(lead.price_indication) : "",
    memo: lead.memo ?? "",
  };
  const [form, setForm] = useState(initial);
  const set = (k: keyof typeof form, v: string) => setForm((f) => ({ ...f, [k]: v }));

  const priceInvalid =
    form.price_indication.trim() !== "" &&
    (!Number.isFinite(Number(form.price_indication)) || Number(form.price_indication) < 0);
  const dirty = (Object.keys(initial) as (keyof typeof form)[]).some(
    (k) => form[k] !== initial[k],
  );
  const converted = Boolean(lead.converted_landlord_id);

  const save = () => {
    if (priceInvalid) return;
    update.mutate(
      {
        first_name: form.first_name || null,
        last_name: form.last_name || null,
        email: form.email || null,
        telephone: form.telephone || null,
        lead_type_id: form.lead_type_id || null,
        owner_user_id: form.owner_user_id || null,
        object_city: form.object_city || null,
        price_indication: form.price_indication ? Number(form.price_indication) : null,
        memo: form.memo || null,
      },
      {
        onSuccess: () => toast.success("Lead saved"),
        onError: () => toast.error("Could not save lead"),
      },
    );
  };

  return (
    <div className="space-y-6">
      {converted && (
        <Badge tone="success">Converted to a landlord</Badge>
      )}

      <section className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <Field label="First name" value={form.first_name} onChange={(v) => set("first_name", v)} />
          <Field label="Last name" value={form.last_name} onChange={(v) => set("last_name", v)} />
          <Field label="Email" value={form.email} onChange={(v) => set("email", v)} />
          <Field label="Phone" value={form.telephone} onChange={(v) => set("telephone", v)} />
          <div className="space-y-1.5">
            <Label>Stage</Label>
            <select className={SELECT} value={form.lead_type_id} onChange={(e) => set("lead_type_id", e.target.value)}>
              <option value="">—</option>
              {(stages ?? []).map((s) => (
                <option key={s.id} value={s.id}>{s.label_en}</option>
              ))}
            </select>
          </div>
          <div className="space-y-1.5">
            <Label>Owner</Label>
            <select className={SELECT} value={form.owner_user_id} onChange={(e) => set("owner_user_id", e.target.value)}>
              <option value="">Unassigned</option>
              {(users ?? []).map((u) => (
                <option key={u.id} value={u.id}>
                  {[u.first_name, u.last_name].filter(Boolean).join(" ") || u.email}
                </option>
              ))}
            </select>
          </div>
          <Field label="Property city" value={form.object_city} onChange={(v) => set("object_city", v)} />
          <div className="space-y-1.5">
            <Label htmlFor="price">Price indication (€)</Label>
            <Input
              id="price"
              type="number"
              min="0"
              value={form.price_indication}
              onChange={(e) => set("price_indication", e.target.value)}
              aria-invalid={priceInvalid}
            />
          </div>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="memo">Memo</Label>
          <textarea
            id="memo"
            rows={3}
            value={form.memo}
            onChange={(e) => set("memo", e.target.value)}
            className="min-h-16 w-full resize-y rounded-md border border-input bg-card p-2.5 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring"
          />
        </div>
        {priceInvalid && <p className="text-xs text-destructive">Price can&rsquo;t be negative.</p>}
        <Button size="sm" disabled={update.isPending || priceInvalid || !dirty} onClick={save}>
          {update.isPending ? "Saving…" : "Save"}
        </Button>
      </section>

      <section className="space-y-3 border-t border-border pt-5">
        <h3 className="text-sm font-medium">Activity</h3>
        <NotesTimeline subjectType="lead" subjectId={lead.id} />
      </section>

      {!converted && (
        <div className="border-t border-border pt-5">
          <Button variant="outline" size="sm" onClick={() => setConfirmConvert(true)}>
            Convert to landlord
          </Button>
        </div>
      )}

      <ConfirmDialog
        open={confirmConvert}
        title="Convert this lead to a landlord?"
        message="Creates a landlord (and a draft property) from this lead. This can't be undone."
        confirmLabel="Convert"
        pending={convert.isPending}
        onConfirm={() => {
          convert.mutate(lead.id, {
            onSuccess: () => {
              toast.success("Converted to landlord");
              setConfirmConvert(false);
              onClose();
            },
            onError: () => toast.error("Convert failed"),
          });
        }}
        onCancel={() => setConfirmConvert(false)}
      />
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <Input value={value} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}

export function LeadDrawer({
  leadId,
  onClose,
}: {
  leadId: string | null;
  onClose: () => void;
}) {
  const { data: lead, isLoading } = useLead(leadId ?? undefined);
  const name = lead
    ? [lead.first_name, lead.last_name].filter(Boolean).join(" ") || "Unnamed lead"
    : "Lead";

  return (
    <Drawer open={Boolean(leadId)} onClose={onClose} title={name} subtitle={lead?.object_city ?? undefined}>
      {isLoading || !lead ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : (
        <LeadBody key={lead.id} lead={lead} onClose={onClose} />
      )}
    </Drawer>
  );
}
